import { orderSourceLabel } from '@/utils/orderSource';
import { pendingBlocksDesign } from '@/utils/offlineEditContinuity';
/**
 * DesignView - Designer workspace
 * Full-height 3-panel layout. Click = view PDF. Drag between panels to manage workflow.
 *
 * Phase 3 D4 (2026-05-17): MW + TO DESIGN are now backed by real
 * server-side assignments. The local `workspaceIds` array is gone —
 * MW membership lives in each order's `assigned_designer_id` field
 * on the backend. Drag-in/out fires `PATCH /orders/{id}/assign`;
 * everything refetches after each mutation.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef, useLayoutEffect, useSyncExternalStore } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Palette, FileText, History, ChevronDown, X, GripVertical, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  DndContext, pointerWithin,
  PointerSensor, useSensor, useSensors,
  DragOverlay, useDroppable
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getNextNDays, formatDateForTab } from '@/utils/dateUtils';
import { API } from '@/config/constants';
import {
  getOrders,
} from '@/api/orders';
import { getUsers } from '@/api/users';
import StatusBadge from '@/components/StatusBadge';
import OfflineInfoBar from '@/components/OfflineInfoBar';
import DesignerBrief from '@/components/DesignerBrief';
import MobileDesignList from '@/components/MobileDesignList';
import MobileCameraSheet from '@/components/MobileCameraSheet';
import { useAuth } from '@/context/AuthContext';
import { isDesktopOffline } from '@/api/desktopSync';
import { getActiveSessionRevision } from '@/api/authenticatedClient';
import { useOrderWorkspaceSession, boundedOrderRead } from '@/hooks/useOrderWorkspaceSession';
import { createDesignDecisions } from '@/hooks/designDecisions';
import DesignFeedback from '@/components/design/DesignFeedback';
import { ArrangementPhotoProvider } from '@/context/ArrangementPhotoContext';

const QUEUE_ID = 'queue';
const WORKSPACE_ID = 'workspace';
// Generic draggable order row used in both panels
const DraggableOrderItem = ({ order, isViewing, onClick, container, children, pending, disabled }) => {
  const offline = isDesktopOffline();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: order.id,
    data: { container },
    disabled: offline || disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}
      className={`flex items-center gap-2 px-2 py-3 rounded-lg transition-all select-none touch-none ${offline ? '' : 'cursor-grab active:cursor-grabbing'} ${
        isViewing ? (container === WORKSPACE_ID ? 'bg-purple-100 border-l-4 border-l-purple-600' : 'bg-stone-200/70')
        : (container === WORKSPACE_ID ? 'hover:bg-purple-50/80 border-l-4 border-l-purple-300' : 'hover:bg-stone-200/50')
      }`}
      data-testid={`${container}-card-${order.id}`}
    >
      <div className="flex-1 min-w-0" onClick={(e) => { e.stopPropagation(); onClick(order); }}>
        {/* Tier 1 (always): recipient name + order#. Stacked (order# under the
            name) on narrow desktop; inline from xl (1280px) up. */}
        <div className="flex flex-col xl:flex-row xl:items-baseline xl:gap-2">
          <span className="text-sm font-semibold text-stone-900 truncate xl:flex-1">{order.recipient_name || 'Unknown'}</span>
          {(() => {
            const m = String(order.internal_order_number || '').match(/-(\d+)$/);
            return m ? <span className="text-[11px] font-mono text-stone-400 xl:flex-shrink-0">#{m[1]}</span> : null;
          })()}
        </div>
        {/* Detail line: appears from xl up. Tier 2 (xl–2xl) shows price + source
            + PDF flag; Tier 3 (2xl / 1536px+) swaps source for the fuller
            product description. Breakpoints are tunable. */}
        <div className="hidden xl:block text-[11px] text-stone-400 truncate mt-0.5">
          {order.total_price && <span className="text-green-700 font-semibold mr-1.5">${order.total_price.toFixed(2)}</span>}
          <span className="2xl:hidden">{orderSourceLabel(order, 'Manual')}</span>
          <span className="hidden 2xl:inline">{order.product_description || orderSourceLabel(order, 'Manual')}</span>
          {order.document_id && <FileText className="h-3 w-3 text-green-600 inline ml-1.5" />}
        </div>
      </div>
      {pending && <span className="text-xs text-stone-500">Pending</span>}
      {children}
    </div>
  );
};

// Drag overlay preview
const DragPreview = ({ order }) => {
  if (!order) return null;
  return (
    <div className="flex items-center gap-2 px-3 py-3 rounded-lg bg-white shadow-xl border border-purple-300 w-56 opacity-90">
      <GripVertical className="h-4 w-4 text-purple-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-stone-900 truncate">{order.recipient_name || 'Unknown'}</div>
        <div className="text-[11px] text-stone-400 truncate">{orderSourceLabel(order, 'Manual')}</div>
      </div>
    </div>
  );
};

// Droppable zone for empty containers
const DroppableZone = ({ id, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id, data: { container: id } });
  // `min-h-0` is essential here: without it, this flex child grows to its
  // content's intrinsic height (because `min-height: auto` is the flex default),
  // which makes the inner `overflow-y-auto` scroll container as tall as its
  // contents — so it never scrolls. With `min-h-0`, the child honors `flex-1`'s
  // calculated space and the inner scroller becomes constrained, scrolling.
  return (
    <div ref={setNodeRef} className={`flex-1 min-h-0 transition-colors ${isOver ? 'bg-purple-100/30' : ''}`}>
      {children}
    </div>
  );
};


const DesignView = () => {
  const session = useOrderWorkspaceSession();
  if (!session.valid) return <div role="status">Design unavailable. Sign in with current access.</div>;
  return <DesignOpening key={session.key} session={session} />;
};

const DesignOpening = ({ session }) => {
  const { user, sessionBinding } = useAuth();
  const isAdmin = useMemo(() => (user?.roles || []).includes('admin'), [user]);
  const offline = session.offline;
  const sessionRef = useRef(session); sessionRef.current = session;
  const life = useRef(false);
  const controllers = useRef(new Set());
  const outcomeToasts = useRef(new Set());
  const readSequence = useRef(0);
  const directorySequence = useRef(0);
  const navigation = useRef(0);
  const drag = useRef(null);
  const read = useCallback((execute, config) => boundedOrderRead(execute, config, controllers.current), []);
  const decisions = useMemo(() => createDesignDecisions(local => sessionRef.current.capture(local), read), [read]);
  const pending = useSyncExternalStore(decisions.subscribe, decisions.getSnapshot);
  const [automaticActions, setAutomaticActions] = useState(() => new Set());
  useLayoutEffect(() => {
    life.current = true; decisions.activate();
    const activeControllers = controllers.current;
    const outcomes = outcomeToasts.current;
    const reads = readSequence; const directories = directorySequence; const nav = navigation;
    return () => {
      life.current = false; reads.current++; directories.current++; nav.current++;
      decisions.retire(); activeControllers.forEach(controller => controller.abort());
      outcomes.forEach(id => toast.dismiss(id)); outcomes.clear();
    };
  }, [decisions]);

  // Workspace owner — the designer whose "My Workspace" is being viewed.
  // Non-admins see only themselves (no dropdown). Admins see a dropdown of
  // every designer; default selection is themselves if they're also a
  // designer, otherwise the first designer in the list.
  const [workspaceOwnerId, setWorkspaceOwnerId] = useState(null);
  const [designers, setDesigners] = useState([]);  // [{id, name, username}, ...]

  const [directoryState, setDirectoryState] = useState('loading');
  const fetchDesigners = useCallback(async () => {
    if (!isAdmin) return;
    const owner = sessionRef.current.capture(false);
    const sequence = ++directorySequence.current;
    const owns = () => life.current && sequence === directorySequence.current && owner?.current();
    if (!owns()) { if (life.current) setDirectoryState('unavailable'); return; }
    setDirectoryState('loading');
    try {
      const users = await read(config => {
        if (!owns()) throw new Error('Directory read retired');
        return getUsers(undefined, config);
      }, owner.config);
      if (!owns()) return;
      if (!Array.isArray(users) || users.some(item => !item || typeof item.id !== 'string' || !Array.isArray(item.roles))) throw new Error('Invalid directory');
      const available = users.filter(item => item.roles.includes('designer') && !item.disabled_at);
      setDesigners(available);
      setWorkspaceOwnerId(previous => available.some(item => item.id === previous) ? previous
        : available.some(item => item.id === user?.id) ? user.id : available[0]?.id || null);
      setDirectoryState('ready');
    } catch {
      if (owns()) setDirectoryState('unavailable');
    }
  }, [isAdmin, read, user?.id]);
  useEffect(() => { fetchDesigners(); }, [fetchDesigners]);

  // Non-admin: workspace owner is always the current user.
  useEffect(() => {
    if (!isAdmin && user?.id) setWorkspaceOwnerId(user.id);
  }, [isAdmin, user]);

  // Resolve display name for the read-only (non-admin) header line.
  const ownerDisplayName = useMemo(() => {
    if (isAdmin) {
      const match = designers.find((d) => d.id === workspaceOwnerId);
      return match?.name || '';
    }
    return user?.name || user?.username || '';
  }, [isAdmin, designers, workspaceOwnerId, user]);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [readState, setReadState] = useState('loading');
  const readStateRef = useRef(readState); readStateRef.current = readState;
  const ordersRef = useRef(orders); ordersRef.current = orders;
  const acquired = useRef(false);
  const [viewingOrder, setViewingOrder] = useState(null);
  // TCA-26101406: accepted note values update both layouts and the reopen source.
  // Receipts only fence list reads started before a note confirmation, not newer
  // server reads or edits from other clients.
  const noteReceipts = useMemo(() => ({ revision: sessionBinding?.revision, sequence: 0, byId: new Map() }), [sessionBinding?.revision]);
  // Photo receipts fence pre-confirmation list reads without claiming authority
  // over later server reads or changing note draft/receipt ownership.
  const photoReceipts = useMemo(() => ({ revision: sessionBinding?.revision, sequence: 0, byId: new Map() }), [sessionBinding?.revision]);
  const handleNoteSaved = useCallback((orderId, text, owns) => {
    if (!life.current || !sessionRef.current.capture(false)?.current() || !owns() || noteReceipts.revision !== getActiveSessionRevision()) return;
    noteReceipts.byId.set(orderId, { sequence: ++noteReceipts.sequence, text });
    const patch = order => order?.id === orderId ? { ...order, designer_notes: text } : order;
    setOrders(previous => owns() ? previous.map(patch) : previous);
    setViewingOrder(previous => owns() ? patch(previous) : previous);
  }, [noteReceipts]);
  const [showCompleted, setShowCompleted] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [photoTapOrder, setPhotoTapOrder] = useState(null);
  const handlePhotoConfirmed = useCallback((orderId, photo, owns) => {
    if (!life.current || !sessionRef.current.capture(false)?.current() || !owns() || photoReceipts.revision !== getActiveSessionRevision()) return;
    photoReceipts.byId.set(orderId, { sequence: ++photoReceipts.sequence, photo });
    const patch = order => order?.id === orderId ? { ...order, arrangement_photo: photo } : order;
    setOrders(previous => owns() ? previous.map(patch) : previous);
    setViewingOrder(previous => owns() ? patch(previous) : previous);
    setPhotoTapOrder(previous => owns() ? patch(previous) : previous);
  }, [photoReceipts]);

  const dateTabs = useMemo(() => getNextNDays(7), []);
  const [selectedDate, setSelectedDate] = useState(dateTabs[0]);
  const [isPastDate, setIsPastDate] = useState(false);
  const selectedDateString = useMemo(() => selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null, [selectedDate]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const context = `${selectedDateString}:${workspaceOwnerId}`;
  const contextRef = useRef(context);
  if (contextRef.current !== context) { contextRef.current = context; navigation.current++; }
  const selectOrder = useCallback(order => { navigation.current++; setViewingOrder(order); }, []);
  const changeDate = (date, past) => {
    navigation.current++; setSelectedDate(date); setIsPastDate(past); selectOrder(null); setPhotoTapOrder(null);
  };
  const changeWorkspace = id => { navigation.current++; setWorkspaceOwnerId(id); selectOrder(null); };

  const fetchOrders = useCallback(async () => {
    const owner = sessionRef.current.capture(false);
    const sequence = ++readSequence.current;
    const startedAt = noteReceipts.sequence;
    const photosStartedAt = photoReceipts.sequence;
    const acknowledged = decisions.getSnapshot().filter(item => item.state === 'accepted').map(item => item.id);
    const owns = () => life.current && owner?.current() && sequence === readSequence.current;
    if (!owns()) { if (life.current) { setReadState('unavailable'); setLoading(false); } return null; }
    setLoading(!acquired.current); setReadState('loading');
    try {
      const loaded = await read(config => {
        if (!owns()) throw new Error('Order read retired');
        return getOrders({ working_view: true }, config);
      }, owner.config);
      if (!owns()) return null;
      if (!Array.isArray(loaded) || loaded.some(item => !item || typeof item.id !== 'string')) throw new Error('Invalid orders');
      const current = loaded.map(order => {
        const receipt = noteReceipts.byId.get(order.id);
        const photo = photoReceipts.byId.get(order.id);
        let result = receipt?.sequence > startedAt ? { ...order, designer_notes: receipt.text } : order;
        if (photo?.sequence > photosStartedAt) result = { ...result, arrangement_photo: photo.photo };
        return result;
      });
      ordersRef.current = current; setOrders(current); acquired.current = true; setReadState('ready');
      // Update observation fields without replacing a detail component's drafts.
      const observations = new Map(current.map(order => [order.id, order]));
      const update = previous => {
        if (!owns() || !previous) return previous;
        const item = observations.get(previous.id);
        return item ? { ...previous, status: item.status, assigned_designer_id: item.assigned_designer_id,
          desktop_sync: item.desktop_sync, arrangement_photo: item.arrangement_photo || null } : previous;
      };
      setViewingOrder(update); setPhotoTapOrder(update);
      decisions.refreshed(acknowledged);
      return current;
    } catch {
      if (owns()) setReadState('unavailable');
      return null;
    } finally { if (owns()) setLoading(false); }
  }, [decisions, noteReceipts, photoReceipts, read]);
  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const dayOrders = useMemo(() => {
    if (!selectedDateString || !orders?.length) return [];
    return orders.filter(o => o.delivery_date === selectedDateString);
  }, [orders, selectedDateString]);

  const allAccepted = useMemo(() => dayOrders.filter(o => o.status === 'accepted'), [dayOrders]);
  const alreadyDone = useMemo(() => dayOrders.filter(o => ['designed', 'on_route', 'delivered'].includes(o.status)), [dayOrders]);

  // MW = accepted orders assigned to the currently-viewed workspace owner.
  // TO DESIGN = accepted orders with NO assignee (unclaimed pool). Orders
  // assigned to a different designer don't show in the current owner's
  // TO DESIGN — they live in that other designer's MW only.
  const workspaceOrders = useMemo(
    () => allAccepted.filter(o => o.assigned_designer_id === workspaceOwnerId && !!workspaceOwnerId),
    [allAccepted, workspaceOwnerId],
  );
  const toDesignQueue = useMemo(
    () => allAccepted.filter(o => o.assigned_designer_id == null),
    [allAccepted],
  );
  const workspaceIds = useMemo(() => workspaceOrders.map(o => o.id), [workspaceOrders]);
  const queueIds = useMemo(() => toDesignQueue.map(o => o.id), [toDesignQueue]);
  const activeOrder = useMemo(() => activeId ? allAccepted.find(o => o.id === activeId) : null, [activeId, allAccepted]);

  const getAcceptedCountForDate = useCallback((date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return orders.filter(o => o.delivery_date === dateStr && o.status === 'accepted').length;
  }, [orders]);

  const handleViewOrder = order => selectOrder(viewingOrder?.id === order.id ? null : order);

  // Find which container an item is in — driven by data, not local state.
  const findContainer = useCallback((id) => {
    if (workspaceIds.includes(id)) return WORKSPACE_ID;
    if (queueIds.includes(id)) return QUEUE_ID;
    return null;
  }, [workspaceIds, queueIds]);

  const signature = order => JSON.stringify([order?.id, order?.status, order?.assigned_designer_id, order?.desktop_sync]);
  const runDecision = async (order, action, options = {}) => {
    const observation = sessionRef.current.capture(order?.desktop_sync?.origin === 'local' || offline);
    const epoch = navigation.current;
    const position = workspaceIds.indexOf(order?.id);
    const wasViewed = viewingOrder?.id === order?.id;
    const wanted = signature(order);
    const current = () => life.current && observation?.current() && readStateRef.current === 'ready'
      && signature(ordersRef.current.find(item => item.id === order?.id)) === wanted && (options.current?.() ?? true);
    if (!current()) return { state: 'blocked' };
    let admitted = false;
    try {
      const result = await decisions.run(order, action, { ...options, current,
        // Only an admitted write retires prior reads; a refused duplicate must not.
        onDispatch: () => {
          readSequence.current++; admitted = true;
          setAutomaticActions(previous => new Set(previous).add(order.id));
        },
      });
      if (!life.current || !observation?.current() || result.state === 'retired') return result;
      if (result.state === 'blocked') return result;
      readSequence.current++;
      if (['accepted', 'refused'].includes(result.state)) {
        const show = result.state === 'accepted' ? toast.success : toast.error;
        const id = show(result.message, {
          onDismiss: () => outcomeToasts.current.delete(id),
          onAutoClose: () => outcomeToasts.current.delete(id),
        });
        if (id !== undefined) outcomeToasts.current.add(id);
      }
      if (result.state === 'unknown') { setReadState('unavailable'); setLoading(false); return result; }
      const currentOrders = await fetchOrders();
      if (!currentOrders || !life.current || !observation.current() || navigation.current !== epoch || !wasViewed
        || result.state !== 'accepted') return result;
      if (action === 'assign') {
        if (options.designerId === null) selectOrder(null);
      } else if (options.returnToList) selectOrder(null);
      else {
        const candidates = currentOrders.filter(item => item.delivery_date === selectedDateString && item.status === 'accepted'
          && item.assigned_designer_id === workspaceOwnerId && !!workspaceOwnerId && item.id !== order.id);
        selectOrder(position >= 0 ? candidates[Math.min(position, candidates.length - 1)] || null : null);
      }
      return result;
    } finally {
      if (admitted && life.current) setAutomaticActions(previous => {
        const next = new Set(previous); next.delete(order.id); return next;
      });
    }
  };
  const handleAssign = (order, designerId, current = () => true) => {
    if (offline || (designerId !== null && isAdmin && (directoryState !== 'ready' || !designers.some(item => item.id === designerId)))) return Promise.resolve({ state: 'blocked' });
    return runDecision(order, 'assign', { designerId, current });
  };
  const handleDragStart = event => {
    const order = allAccepted.find(item => item.id === event.active.id);
    if (!order || decisions.blocked(order.id) || offline || readState !== 'ready') return;
    drag.current = { id: order.id, epoch: navigation.current, signature: signature(order) }; setActiveId(order.id);
  };
  const handleDragEnd = async ({ active, over }) => {
    const start = drag.current; drag.current = null; setActiveId(null);
    if (!start || start.id !== active.id || start.epoch !== navigation.current || offline || !over) return;
    const source = findContainer(active.id);
    const target = over.data?.current?.container || findContainer(over.id);
    const order = allAccepted.find(item => item.id === active.id);
    if (!source || ![QUEUE_ID, WORKSPACE_ID].includes(target) || source === target || signature(order) !== start.signature) return;
    if (target === WORKSPACE_ID && !workspaceOwnerId) return;
    await handleAssign(order, target === WORKSPACE_ID ? workspaceOwnerId : null);
  };
  const handleRemoveFromWorkspace = id => handleAssign(allAccepted.find(item => item.id === id), null);
  const handleMarkDesigned = (id, options = {}) => runDecision(allAccepted.find(item => item.id === id), 'complete', options);
  const actionBlocked = order => readState !== 'ready' || !order || decisions.blocked(order.id)
    || order.status !== 'accepted' || (offline ? pendingBlocksDesign(order) : !!order.desktop_sync?.pending_mutation);
  const acknowledge = async id => {
    if (await fetchOrders()) decisions.acknowledge(id);
  };

  const documentUrl = viewingOrder ? `${API}/orders/${viewingOrder.id}/document?stamp=1` : null;

  return (
    <ArrangementPhotoProvider orders={orders} onPhotoConfirmed={handlePhotoConfirmed}>
    <div className="h-[calc(100vh-48px)] flex flex-col" data-testid="design-view">

      {/* Top bar */}
      <div className="flex items-center gap-3 px-3 py-1.5 bg-white border-b border-stone-200 flex-shrink-0">
        <span className="text-sm font-bold text-stone-800">Design</span>
        <div className="hidden md:flex items-center gap-1">
          {dateTabs.slice(0, 5).map((date) => {
            const isSelected = selectedDateString === format(date, 'yyyy-MM-dd') && !isPastDate;
            const count = getAcceptedCountForDate(date);
            return (
              <Button key={date.toISOString()} variant={isSelected ? 'default' : 'ghost'} size="sm"
                onClick={() => changeDate(date, false)}
                className="h-6 text-xs px-2" data-testid={`design-date-${format(date, 'yyyy-MM-dd')}`}
              >
                {formatDateForTab(date)}
                {count > 0 && <span className={`ml-1 text-[10px] px-1 rounded-full ${isSelected ? 'bg-white/20' : 'bg-amber-100 text-amber-700'}`}>{count}</span>}
              </Button>
            );
          })}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant={isPastDate ? 'default' : 'ghost'} size="sm" className="h-6 text-xs px-2 gap-1" data-testid="design-past-date-btn">
                <History className="h-3 w-3" />{isPastDate ? formatDateForTab(selectedDate) : 'Past'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={selectedDate} onSelect={date => { if (date) changeDate(date, true); }} initialFocus />
            </PopoverContent>
          </Popover>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-stone-500 ml-auto">
          <Palette className="h-3.5 w-3.5 text-purple-600" />
          <span className="font-semibold text-stone-700" aria-label={readState === 'ready' ? undefined : 'Current count unavailable'}>{readState === 'ready' ? allAccepted.length : '—'}</span>
          <span>to design</span>
          {alreadyDone.length > 0 && <span className="text-stone-400">({alreadyDone.length} done)</span>}
        </div>
        <DesignFeedback readState={readState} acquired={acquired.current}
          directoryState={isAdmin ? directoryState : 'ready'} pending={pending} automaticActions={automaticActions}
          refreshOrders={fetchOrders} refreshDesigners={fetchDesigners}
          review={id => decisions.review(id)} acknowledge={acknowledge}
          isCurrent={() => life.current && !!sessionRef.current.capture(offline)?.current()} />
      </div>

      <OfflineInfoBar />

      {/* Mobile layout: MobileDesignList + brief overlay */}
      <div className="md:hidden flex-1 flex flex-col overflow-hidden relative">
        <MobileDesignList
          myOrders={workspaceOrders}
          allOrders={allAccepted}
          doneOrders={alreadyDone}
          dateTabs={dateTabs}
          selectedDate={selectedDate}
          isPastDate={isPastDate}
          loading={loading}
          isAdmin={isAdmin}
          designers={designers}
          onDateChange={changeDate}
          onSelectOrder={handleViewOrder}
          onPhotoTap={setPhotoTapOrder}
          onRefresh={fetchOrders}
          onAssign={handleAssign}
          actionBlocked={actionBlocked}
          directoryState={directoryState}
          readState={readState}
          offline={offline}
          contextKey={context}
        />
        {viewingOrder && (
          <div className="absolute inset-0 z-30 bg-white flex flex-col overflow-hidden">
            <DesignerBrief
              variant="mobile"
              order={viewingOrder}
              actionPending={actionBlocked(viewingOrder)}
              onNoteSaved={handleNoteSaved}
              onMarkDesigned={(id) => handleMarkDesigned(id, { returnToList: true })}
              documentUrl={documentUrl}
              onBack={() => selectOrder(null)}
            />
          </div>
        )}
      </div>

      {photoTapOrder && (
        <MobileCameraSheet
          key={photoTapOrder.id}
          order={photoTapOrder}
          onClose={() => setPhotoTapOrder(null)}
        />
      )}

      {/* Desktop layout: DnD panels + brief sidebar */}
      <div className="hidden md:flex flex-1 overflow-hidden relative">
        <DndContext
          sensors={sensors}
          collisionDetection={pointerWithin}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={() => { drag.current = null; setActiveId(null); }}
        >
          {/* Left panel: To Design queue */}
          <div className="w-[clamp(8.5rem,14vw,13rem)] flex-shrink-0 border-r border-stone-200 bg-stone-50 flex flex-col overflow-hidden">
            <div className="px-3 pt-1.5 pb-1.5 border-b border-stone-200 bg-stone-100/50 space-y-1">
              {/* Empty top row matches the workspace owner-row height so the
                  TO DESIGN label aligns horizontally with MY WORKSPACE. */}
              <div className="h-6" aria-hidden="true" />
              <span className="block text-[10px] font-semibold text-stone-500 uppercase tracking-wider">To Design ({readState === 'ready' ? toDesignQueue.length : '—'})</span>
            </div>
            <DroppableZone id={QUEUE_ID}>
              <div className="overflow-y-auto p-2 space-y-1 h-full">
                <SortableContext items={queueIds} strategy={verticalListSortingStrategy}>
                  {loading ? (
                    <div className="text-xs text-stone-400 text-center py-8">Loading...</div>
                  ) : toDesignQueue.length === 0 ? (
                    <div className="text-xs text-stone-400 text-center py-8">
                      {readState !== 'ready' ? 'Current queue not established' : allAccepted.length === 0 ? 'All caught up!' : 'All assigned'}
                    </div>
                  ) : (
                    toDesignQueue.map((order) => (
                      <DraggableOrderItem
                        key={order.id}
                        order={order}
                        disabled={actionBlocked(order)}
                        pending={pending.some(item => item.id === order.id)}
                        isViewing={viewingOrder?.id === order.id}
                        onClick={handleViewOrder}
                        container={QUEUE_ID}
                      />
                    ))
                  )}
                </SortableContext>
                {/* Already done */}
                {alreadyDone.length > 0 && (
                  <div className="pt-2 mt-2 border-t border-stone-200">
                    <button onClick={() => setShowCompleted(!showCompleted)} className="flex items-center gap-1 text-[10px] text-stone-400 hover:text-stone-600 w-full px-2">
                      <ChevronDown className={`h-3 w-3 transition-transform ${showCompleted ? 'rotate-180' : ''}`} />
                      {alreadyDone.length} done
                    </button>
                    {showCompleted && (
                      <div className="mt-1 space-y-0.5">
                        {alreadyDone.map((order) => (
                          <div key={order.id}
                            className={`flex items-center gap-1.5 px-2 py-2 text-xs rounded cursor-pointer transition-colors ${
                              viewingOrder?.id === order.id ? 'bg-stone-200/70' : 'hover:bg-stone-100'
                            }`}
                            onClick={() => handleViewOrder(order)}
                          >
                            <StatusBadge status={order.status} size="sm" />
                            <span className="truncate text-stone-500">{order.recipient_name || '?'}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </DroppableZone>
          </div>

          {/* Middle panel: Workspace */}
          <div className="w-[clamp(9.5rem,18vw,18rem)] flex-shrink-0 border-r border-stone-200 bg-purple-50/20 flex flex-col overflow-hidden">
            <div className="px-3 pt-1.5 pb-1.5 border-b border-stone-200 bg-purple-50/50 space-y-1">
              {/* Owner row — admin gets a dropdown, everyone else sees their name.
                  When an admin is viewing a colleague's workspace (i.e. the
                  selected owner is *not* themselves) the Eye icon appears as
                  a subtle "this isn't yours" cue — designed to lower the
                  chance of accidental edits on someone else's work. */}
              <div className="h-6 flex items-center gap-1.5" data-testid="workspace-owner-row">
                {isAdmin && workspaceOwnerId && workspaceOwnerId !== user?.id && (
                  <Eye
                    className="h-3 w-3 text-purple-500 flex-shrink-0"
                    aria-label="Viewing another designer's workspace"
                    data-testid="workspace-viewing-as-admin-indicator"
                  >
                    <title>Viewing another designer's workspace</title>
                  </Eye>
                )}
                {isAdmin ? (
                  <Select
                    value={workspaceOwnerId || ''}
                    onValueChange={changeWorkspace}
                    disabled={directoryState !== 'ready' || designers.length === 0}
                  >
                    <SelectTrigger
                      className="h-6 px-2 py-0 text-xs font-medium text-stone-700 border-stone-300 bg-white/70 hover:bg-white focus:ring-1 focus:ring-purple-300 w-full"
                      data-testid="workspace-owner-select-trigger"
                    >
                      <SelectValue placeholder={directoryState === 'loading' ? 'Checking designers…' : directoryState !== 'ready' ? 'Designers unavailable' : designers.length === 0 ? 'No designers' : 'Select designer'} />
                    </SelectTrigger>
                    <SelectContent>
                      {designers.map((d) => (
                        <SelectItem key={d.id} value={d.id} data-testid={`workspace-owner-option-${d.id}`}>
                          {d.name || d.username}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <span
                    className="text-xs font-medium text-stone-700 truncate"
                    data-testid="workspace-owner-name"
                    title={ownerDisplayName}
                  >
                    {ownerDisplayName || '—'}
                  </span>
                )}
              </div>
              <span className="block text-[10px] font-semibold text-purple-600 uppercase tracking-wider">Assigned Orders ({readState === 'ready' ? workspaceOrders.length : '—'})</span>
            </div>
            <DroppableZone id={WORKSPACE_ID}>
              <div className="overflow-y-auto p-2 space-y-1 h-full">
                <SortableContext items={workspaceIds} strategy={verticalListSortingStrategy}>
                  {workspaceOrders.length === 0 && !activeId ? (
                    <div className="text-xs text-purple-300 text-center py-8">
                      {readState === 'ready' ? 'Drag orders here' : 'Current workspace not established'}
                    </div>
                  ) : (
                    workspaceOrders.map((order) => (
                      <DraggableOrderItem
                        key={order.id}
                        order={order}
                        disabled={actionBlocked(order)}
                        pending={pending.some(item => item.id === order.id)}
                        isViewing={viewingOrder?.id === order.id}
                        onClick={handleViewOrder}
                        container={WORKSPACE_ID}
                      >
                        {!offline && (
                          <div className="flex items-center flex-shrink-0" onClick={e => e.stopPropagation()}>
                            <button disabled={actionBlocked(order) || offline} onClick={() => handleRemoveFromWorkspace(order.id)} className="text-stone-300 hover:text-red-500 p-1" data-testid={`workspace-remove-${order.id}`}>
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        )}
                      </DraggableOrderItem>
                    ))
                  )}
                </SortableContext>
              </div>
            </DroppableZone>
          </div>

          {/* Drag overlay */}
          <DragOverlay>
            <DragPreview order={activeOrder} />
          </DragOverlay>
        </DndContext>

        {/* Right: Designer Brief detail viewer */}
        <div className="flex flex-1 flex-col overflow-hidden bg-white">
          {viewingOrder ? (
            <DesignerBrief actionPending={actionBlocked(viewingOrder)} order={viewingOrder} onNoteSaved={handleNoteSaved} onMarkDesigned={handleMarkDesigned} documentUrl={documentUrl} />
          ) : (
            <div className="flex-1 flex items-center justify-center text-stone-300">
              <div className="text-center">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Click an order to view its details</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </ArrangementPhotoProvider>
  );
};

export default DesignView;
